import { flattenDeep, intersection } from 'lodash';
import { Category } from '@reverbdotcom/commons/src/gql/graphql';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import { buyingGuideEligibleRegionAndLocale } from './discovery/user_helper';

const mappings = [
  {
    brand: 'Fender',
    model: 'Stratocaster',
    slug: 'stratocaster-buying-guide',
  },
  {
    brand: 'Fender',
    model: 'Telecaster',
    slug: 'buying-guide-telecasters',
  },
  {
    brand: 'Fender',
    model: 'Precision',
    slug: 'precision-bass-buying-guide',
  },
  {
    brand: 'Gibson',
    model: 'Les Paul',
    slug: 'buying-guide-gibson-les-pauls',
  },
  {
    brand: 'Korg',
    model: 'Volca',
    slug: 'buying-guide-korg-volca-series',
  },
  {
    brand: 'Electro-Harmonix',
    model: 'Big Muff',
    slug: 'big-muff-buying-guide',
  },
  {
    brand: 'Epiphone',
    model: 'Les Paul',
    slug: 'which-epiphone-les-paul-should-i-buy',
  },
  {
    brand: 'Universal Audio',
    model: 'Apollo',
    slug: 'buying-guide-universal-audio-apollo-series',
  },
  {
    brand: 'Roland',
    model: 'JV',
    slug: 'jv-series-synthesizers-and-expansion-cards',
  },
  {
    brand: 'Roland',
    model: 'Boutique Series',
    slug: 'buying-guide-roland-boutique-series',
  },
  {
    brand: 'Paul Reed Smith',
    model: 'SE',
    slug: 'buying-guide-prs-se-series',
  },
  {
    brand: 'Ibanez',
    model: 'tube screamer',
    slug: 'buying-guide-the-ibanez-tube-screamer',
  },
  {
    model: 'USB',
    categorySlugs: ['pro-audio', 'microphones'],
    slug: 'buying-guide-podcasting-gear',
  },
  {
    model: 'Boost',
    categorySlugs: ['effects-and-pedals', 'overdrive-and-boost'],
    slug: 'buying-guide-boost-pedals',
  },
  {
    model: 'Monosynth',
    categorySlugs: ['keyboards-and-synths'],
    slug: 'buying-guide-affordable-monosynths',
  },
  {
    categorySlugs: ['acoustic-guitars'],
    slug: 'buying-guide-acoustic-guitars',
  },
  {
    categorySlugs: ['drums-and-percussion', 'snare'],
    slug: 'snare-drum-buying-guide',
  },
  {
    categorySlugs: ['pro-audio', '500-series'],
    slug: 'buying-guide-essential-500-series-modules',
  },
  {
    categorySlugs: ['folk-instruments', 'mandolins'],
    slug: 'buying-guide-mandolins',
  },
  {
    categorySlugs: ['effects-and-pedals', 'delay'],
    slug: 'buying-guide-how-to-choose-a-delay-pedal',
  },
  {
    categorySlugs: ['pro-audio', 'interfaces'],
    slug: 'buying-guide-audio-interfaces',
  },
  {
    categorySlugs: ['keyboards-and-synths', 'drum-machines'],
    slug: 'drum-machine-buying-guide',
  },
  {
    categorySlugs: ['keyboards-and-synths', 'samplers'],
    slug: 'drum-machine-buying-guide',
  },
  {
    categorySlugs: ['pro-audio', 'mixers'],
    slug: 'buying-guide-basic-pa-systems',
  },
  {
    categorySlugs: ['pro-audio', 'powered-mixers'],
    slug: 'buying-guide-basic-pa-systems',
  },
  {
    categorySlugs: ['pro-audio', 'portable-pa-systems'],
    slug: 'buying-guide-basic-pa-systems',
  },
  {
    categorySlugs: ['accessories', 'capos'],
    slug: 'buying-guide-capos',
  },
  {
    categorySlugs: ['pro-audio', 'microphone-preamps'],
    slug: 'buying-guide-preamps',
  },
  {
    categorySlugs: ['pro-audio', 'studio-monitors'],
    slug: 'buying-guide-studio-monitors',
  },
  {
    categorySlugs: ['amps', 'bass-heads'],
    slug: 'buying-guide-portable-bass-amp-heads',
  },
  {
    categorySlugs: ['folk-instruments', 'ukuleles'],
    slug: 'buying-guide-ukuleles',
  },
  {
    categorySlugs: ['effects-and-pedals', 'reverb'],
    slug: 'buying-guide-reverb-pedals',
  },
  {
    categorySlugs: ['parts', 'acoustic-pickups'],
    slug: 'buying-guide-acoustic-pickups',
  },
  {
    categorySlugs: ['effects-and-pedals', 'pedalboards-and-power-supplies'],
    slug: 'buying-guide-pedalboard-power-supply',
  },
];

function matchBrandAndModel(brand_name, model, mapping) {
  if (!mapping.brand || !mapping.model) return;
  return brand_name === mapping.brand &&
         !!model.match(new RegExp(mapping.model, 'ig'));
}

function matchCategoriesAndModel(categories: Category[], model, mapping) {
  if (!mapping.categorySlugs || !mapping.model) return;
  const intersectingCategories = intersection(
    mapping.categorySlugs,
    categorySlugs(categories),
  );
  return intersectingCategories.length === mapping.categorySlugs.length &&
         !!model.match(new RegExp(mapping.model, 'ig'));
}

function matchCategories(categories: Category[], mapping) {
  if (mapping.model) return;
  if (!mapping.categorySlugs) return;
  const intersectingCategories = intersection(
    mapping.categorySlugs,
    categorySlugs(categories),
  );
  return intersectingCategories.length === mapping.categorySlugs.length;
}

function categorySlugs(categories: Category[]) {
  if (!categories || !categories.length) return [];
  const categorySlugs = categories.map((cat) => {
    const parentSlugs = cat.parentCategories && cat.parentCategories.map((parentCategory) => {
      return parentCategory.slug;
    });

    return [cat.slug].concat(parentSlugs || []);
  });
  return flattenDeep(categorySlugs);
}

export default function relatedBuyingGuide(brand_name: string, model: string, categories: Category[], user:Partial<IUser>) {
  if (!buyingGuideEligibleRegionAndLocale(user.shippingRegionCode, user.locale)) return null;

  const match = mappings.find((mapping) => {
    return matchBrandAndModel(brand_name, model, mapping) ||
           matchCategoriesAndModel(categories, model, mapping) ||
           matchCategories(categories, mapping);
  });
  if (!match) return null;

  return match.slug;
}
